import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Flex, Select, Tooltip, Modal, Tag } from 'antd';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { APIClinic } from 'lib/api/firebase/apis/api-clinic-balance';
import {
  addClinician,
  clinicActionEditClinicianDetails
} from 'lib/api/firebase/apis/apis';
import { Clinician } from 'lib/documents/clinician';
import { useGetCliniciansList } from 'lib/hooks/tanstack/use-GetCliniciansList';
import { useGetPrincipalUser } from 'lib/hooks/tanstack/use-getClinicInfo';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useAppSelector } from 'lib/store/rootReducer';
import { useErrorList } from 'lib/utils/useErrorList';
import { useMemo, useState } from 'react';
import { useCopyToClipboardMutation } from '../../../../../lib/hooks/tanstack/use-CopyToClipboardMutation';
import { BASE_USER_ADD_QUESTIONS } from '../../types/form-questions';

import {
  ActionState,
  ActionTagSettings
} from 'components/action-tag/action-tag-component';
import { TagEditDetails } from 'features/clinician/clinic-user-management/components/tag-edit-details';
import { useQueryDataErrors } from 'lib/hooks/use-query-data-errors';
import { ACTION_COPYINVITELINK } from './configs/config-action-copyinvitelink';
import { BASE_ACTIONS } from './configs/shared-actions-baseconfig';
import UserTable from './user-table';
import { TableRow } from './user-table-types';
import { listUserResponseToTableRow } from './utils/listUserResponseToTableRow';

interface SetPrincipalUserActionProps {
  onConfirm: () => void;
  isLoading: boolean;
}

function SetPrincipalUserAction({
  onConfirm,
  isLoading
}: SetPrincipalUserActionProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Transfer Ownership',
      content: (
        <div>
          <p>
            You are transferring ownership of the organisation over to this
            user.
          </p>
          <p>Are you sure you want to continue?</p>
        </div>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        onConfirm();
      }
    });
  };

  return (
    <span
      onClick={handleClick}
      style={{ cursor: 'pointer', display: 'block', width: '100%' }}
    >
      {isLoading ? 'Setting as Principal...' : 'Set as Principal User'}
    </span>
  );
}

function RemoveClinicianAction({
  record,
  onConfirm,
  isLoading,
  tagStyles
}: {
  record: TableRow<
    Clinician & {
      firstName: string;
      lastName: string;
      invitePending: boolean;
    }
  >;
  onConfirm: () => void;
  isLoading: boolean;
  tagStyles: any;
}) {
  const role = (record.raw as any).role;
  const roleName = role === 'ClinicAdmin' ? 'Clinic Admin' : 'Clinician';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    Modal.confirm({
      title: `Remove ${roleName}`,
      content: (
        <div>
          <p>
            Are you sure you want to remove {record.firstName} {record.lastName} from this clinic?
          </p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        onConfirm();
      }
    });
  };

  return (
    <Tag
      {...tagStyles}
      color={isLoading ? tagStyles.color : 'error'}
      className="clickable"
      style={{ cursor: 'pointer', padding: '0.1rem', width: '100%', textAlign: 'center' }}
      onClick={handleClick}
    >
      {isLoading ? 'Removing...' : `Remove ${roleName}`}
    </Tag>
  );
}

interface ClinicianRoleSelectProps {
  clinicId: string;
  record: TableRow<
    Clinician & {
      firstName: string;
      lastName: string;
      invitePending: boolean;
    }
  >;
  query: any;
  queryPendingUsers: any;
  errorList: any;
  principalUser?: any;
}

function ClinicianRoleSelect({
  clinicId,
  record,
  query,
  queryPendingUsers,
  errorList,
  principalUser
}: ClinicianRoleSelectProps) {
  const currentUser = useAppSelector(state => state.auth.user);
  const currentRole = (record.raw as any).role;
  const isSelf = record.raw.id === currentUser?.uid;
  const isPending = record.raw.invitePending;
  const isPrincipal = record.raw.id === principalUser?.uid;
  const [showStatus, setShowStatus] = useState<'success' | 'error' | null>(
    null
  );

  const mutation = useMutation({
    mutationFn: (newRole: 'ClinicAdmin' | 'Clinician') =>
      APIClinic.changeRole({
        clinicId,
        uid: record.raw.id,
        role: newRole
      }),
    onSuccess: () => {
      query.refetch();
      queryPendingUsers.refetch();
      setShowStatus('success');
      setTimeout(() => setShowStatus(null), 3000);
    },
    onError: error => {
      errorList.addError(error as Error, 'changeRole');
      setShowStatus('error');
      setTimeout(() => setShowStatus(null), 3000);
    }
  });

  const selectEl = (
    <Select
      value={currentRole}
      disabled={isSelf || isPending || isPrincipal}
      loading={mutation.isPending}
      onChange={async value => {
        mutation.mutate(value as 'ClinicAdmin' | 'Clinician');
      }}
      options={[
        { label: 'Clinician', value: 'Clinician' },
        { label: 'Clinic Admin', value: 'ClinicAdmin' }
      ]}
      style={{ width: 150 }}
    />
  );

  const wrapper = (
    <Flex align="center" gap={8}>
      {selectEl}
      {showStatus === 'success' && (
        <CheckOutlined style={{ color: '#52c41a' }} />
      )}
      {showStatus === 'error' && <CloseOutlined style={{ color: '#ff4d4f' }} />}
    </Flex>
  );

  if (isSelf) {
    return (
      <Tooltip title="You cannot change your own role">
        <span style={{ display: 'inline-block' }}>{wrapper}</span>
      </Tooltip>
    );
  }

  if (isPending) {
    return (
      <Tooltip title="You cannot change the role of a pending invite">
        <span style={{ display: 'inline-block' }}>{wrapper}</span>
      </Tooltip>
    );
  }

  if (isPrincipal) {
    return (
      <Tooltip title="You cannot change the role of the principal user">
        <span style={{ display: 'inline-block' }}>{wrapper}</span>
      </Tooltip>
    );
  }

  return wrapper;
}

export function ClinicianTable(props: { clinicId: string }) {
  const query = useGetCliniciansList(props.clinicId, false);
  const queryPendingUsers = useGetCliniciansList(props.clinicId, true);
  const principalUserQuery = useGetPrincipalUser(props.clinicId);
  const errorList = useErrorList();
  const copyToClipboardMutation = useCopyToClipboardMutation();
  const currentUser = useAppSelector(state => state.auth.user);

  const setPrincipalMutation = useMutation({
    mutationFn: (uid: string) =>
      APIClinic.setPrincipalUser({
        clinicId: props.clinicId,
        uid
      }),
    onSuccess: () => {
      principalUserQuery.refetch();
    }
  });

  useQueryErrorNotifier(query, errorList, 'ConfirmedUsers');
  useQueryErrorNotifier(principalUserQuery, errorList, 'PrincipalUser');
  useQueryErrorNotifier(queryPendingUsers, errorList, 'PendingUsers');
  useQueryErrorNotifier(copyToClipboardMutation, errorList, 'CopyToClipboard');
  useQueryDataErrors({
    query: query,
    errorList,
    contextPrefix: 'ConfirmedUsers: '
  });
  useQueryDataErrors({
    query: queryPendingUsers,
    errorList,
    contextPrefix: 'PendingUsers: '
  });

  const data = useMemo(
    () => [
      ...(query.data?.pages ?? []).flatMap(_ =>
        listUserResponseToTableRow(_.data, { invitePending: false })
      ),
      ...(queryPendingUsers.data?.pages ?? []).flatMap(_ =>
        listUserResponseToTableRow(_.data, { invitePending: true })
      )
    ],
    [query.data, queryPendingUsers.data]
  );

  return (
    <div>
      <ErrorListDisplay errorList={errorList} closeable />
      <UserTable<
        Clinician & {
          firstName: string;
          lastName: string;
          invitePending: boolean;
        }
      >
        principalUser={principalUserQuery.data?.data}
        typeUnit="Clinician"
        onAddUser={(user, clinicId) => addClinician({ ...user, clinicId })}
        data={data}
        isLoading={query.isLoading}
        refetchData={() => {
          query.refetch();
          queryPendingUsers.refetch();
        }}
        onError={(error, metadata) =>
          errorList.addError(error, `${metadata.action} - ${metadata.id}`)
        }
        clinicId={props.clinicId}
        extraColumns={[
          {
            title: 'Role',
            key: 'changeRole',
            render: (_value, record) => (
              <ClinicianRoleSelect
                clinicId={props.clinicId}
                record={record}
                query={query}
                queryPendingUsers={queryPendingUsers}
                errorList={errorList}
                principalUser={principalUserQuery.data?.data}
              />
            )
          }
        ]}
        actions={[
          {
            name: 'Set as Principal',
            initialState: () => ActionState.NEUTRAL,
            shouldShow: record => {
              const isCurrentUserPrincipal =
                principalUserQuery.data?.data?.uid === currentUser?.uid;
              const isRowPrincipal =
                principalUserQuery.data?.data?.uid === record.raw.id;
              return !!isCurrentUserPrincipal && !isRowPrincipal && !(record.raw as any).invitePending;
            },
            render: _props => (
              <SetPrincipalUserAction
                onConfirm={() => _props.onClick(_props.record)}
                isLoading={_props.disabled}
              />
            ),
            onClick: async record => {
              await setPrincipalMutation.mutateAsync(record.raw.id);
            }
          },
          {
            name: 'Edit',
            initialState: () => ActionState.NEUTRAL,
            render: _props => (
              <TagEditDetails
                {..._props}
                userAddQuestions={BASE_USER_ADD_QUESTIONS}
                initialValues={{
                  firstName: _props.record.firstName,
                  lastName: _props.record.lastName,
                  email: _props.record.email
                }}
              />
            ),
            onClick: p => {
              if (!p.raw.id) {
                throw new Error(
                  'ID is undefined. Please try reloading the page'
                );
              }

              return clinicActionEditClinicianDetails({
                uid: p.raw.id,
                firstName: p.firstName,
                lastName: p.lastName,
                email: p.email
              });
            },
            onSuccess: refetch => refetch()
          },
          ...(BASE_ACTIONS as unknown as ActionTagSettings<
            TableRow<
              Clinician & {
                invitePending: boolean;
                firstName: string;
                lastName: string;
              }
            >
          >[]),
          {
            name: 'Remove User From Organisation',
            initialState: () => ActionState.NEUTRAL,
            shouldShow: record => {
              const isCurrentUserPrincipal =
                principalUserQuery.data?.data?.uid === currentUser?.uid;
              const isSelf = record.raw.id === currentUser?.uid;
              const isRowPrincipal =
                principalUserQuery.data?.data?.uid === record.raw.id;
              return !!isCurrentUserPrincipal && !isSelf && !isRowPrincipal;
            },
            render: _props => (
              <RemoveClinicianAction
                record={_props.record}
                onConfirm={() => _props.onClick(_props.record)}
                isLoading={_props.disabled}
                tagStyles={_props.tagStyles}
              />
            ),
            onClick: async record => {
              const role = (record.raw as any).role;
              if (record.invitePending === true) {
                await APIClinic.removePatientInvite({
                  clinicId: props.clinicId,
                  invitationId: record.raw.id
                })
                return;
              }
              
              if (role === 'ClinicAdmin') {
                await APIClinic.removeClinicAdmin({
                  clinicId: props.clinicId,
                  adminId: record.raw.id
                });
              } else {
                await APIClinic.removeClinician({
                  clinicId: props.clinicId,
                  clinicianId: record.raw.id
                });
              }
            },
            onSuccess: refetch => refetch()
          },
          ACTION_COPYINVITELINK(copyToClipboardMutation.mutateAsync)
        ]}
        userAddQuestions={BASE_USER_ADD_QUESTIONS}
      />
    </div>
  );
}
